import { useState, useMemo } from "react";
import CalculatorShell from "@/components/CalculatorShell";
import Field from "@/components/Field";
import ResultStat from "@/components/ResultStat";
import { toNum } from "@/lib/finance";
import { fmtUSD } from "@/lib/format";

export default function SimpleInterestPage() {
    const [principal, setPrincipal] = useState(5000);
    const [rate, setRate] = useState(5);
    const [years, setYears] = useState(3);

    const { interest, total } = useMemo(() => {
        const p = toNum(principal);
        const r = toNum(rate) / 100;
        const t = toNum(years);
        const interest = p * r * t;
        return { interest, total: p + interest };
    }, [principal, rate, years]);

    return (
        <CalculatorShell
            slug="simple-interest"
            explainer={
                                <>
                    <h2>How this simple interest calculator works</h2>
                    <p>
                        A simple interest calculator computes earnings or charges based only on the original
                        principal — there is no compounding. It's used for short-term loans, some auto loans,
                        certain bonds, and Treasury bills where interest doesn't compound on itself.
                    </p>
                    <h3>How it works</h3>
                    <ul>
                        <li><strong>Simple interest formula:</strong> I = P × r × t, where P is principal, r is annual rate, and t is time in years.</li>
                        <li><strong>Total amount:</strong> principal plus interest — the full amount owed or received at the end of the term.</li>
                        <li><strong>Flat interest:</strong> the interest amount stays the same each period because it never earns interest on itself.</li>
                        <li><strong>Partial years:</strong> pro-rated as a fraction — for example, six months equals 0.5 in the formula.</li>
                        <li><strong>Common applications:</strong> auto loans, payday loans, short-term personal loans, and Treasury bills.</li>
                    </ul>
                    <h3>Tips</h3>
                    <ul>
                        <li>Simple interest is better for borrowers and worse for savers compared to compound interest — know which you're dealing with.</li>
                        <li>Verify your loan type before signing; some loans advertised as simple interest still compound if payments are late.</li>
                        <li>For savings, always favor accounts with compounding — even daily compounding at the same rate beats simple interest over time.</li>
                    </ul>
                </>
            }
        >
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                <div className="lg:col-span-3 bg-card rounded-xl border border-border p-6 sm:p-8 space-y-5">
                    <Field label="Principal" prefix="$" value={principal} onChange={setPrincipal} dataTestId="calc-input-principal" />
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Interest rate" suffix="%" value={rate} onChange={setRate} step={0.1} dataTestId="calc-input-rate" />
                        <Field label="Years" value={years} onChange={setYears} step={0.5} dataTestId="calc-input-years" />
                    </div>
                </div>
                <div className="lg:col-span-2">
                    <ResultStat
                        label="Total after interest"
                        value={total}
                        format={(n) => fmtUSD(n)}
                        secondary={[
                            { label: "Interest earned", value: interest, format: (n) => fmtUSD(n), testId: "calc-result-interest" },
                        ]}
                    />
                </div>
            </div>
        </CalculatorShell>
    );
}
